/**
 * useLocalAnalyzer — the v2.4 Client Analyzer engine: 15-parameter local
 * measurement table, 1 s WebRTC getStats() polling, 60-sample history rings
 * (the 60 s sparklines), clip latch + worklet/disk telemetry.
 *
 * Spec rows (docs/rt-ui-v2-spec.md §Client Analyzer 精确参数表):
 *   network 1-8  — sender stats (candidate-pair / outbound-rtp /
 *                  remote-inbound-rtp) + first remote mic receiver stats +
 *                  program-video receiver stats (actor)
 *   audio   9-11 — AudioContext.sampleRate probe · worklet dropouts ·
 *                  input peak dBFS + latched clip counter
 *   disk   12-15 — recorder state/bytes · commit latency · storage.estimate()
 *
 * Every tick also publishes the 8-item heartbeat summary to the analyzer bus
 * (use-rt-room broadcasts it inside the 5 s status message).
 */
import type { RemoteTrack, Room } from 'livekit-client'
import { Track } from 'livekit-client'
import { useEffect, useRef, useState } from 'react'
import type { RtRole } from '../lib/rt-protocol'
import { setAnalyzerSummary } from './analyzer-bus'
import type { RecorderState } from './recorder-bridge'

export interface RecorderDiag {
  droppedFrames: number
  lastCommitMs: number | null
}

export interface AnalyzerSnap {
  // network (1-8)
  rttMs: number | null
  lossUpPct: number | null
  lossDownPct: number | null
  jitterMs: number | null
  sendKbps: number | null
  availOutKbps: number | null
  /** 3 consecutive falling samples → 'down' (yellow per spec) */
  availTrend: 'down' | 'flat' | null
  linkType: string | null
  vidRecvKbps: number | null
  vidFramesDropped: number | null
  vidFreezeCount: number | null
  // audio (9-11)
  sampleRate: number | null
  workletDropped: number | null
  peakDbfs: number
  clipCount: number
  // recording / disk (12-15)
  recState: RecorderState
  recBytes: number
  /** REC with no byte growth for >3 s → write stall (red) */
  recStalled: boolean
  commitMs: number | null
  diskFreeGB: number | null
  diskNeedGB: number
}

export interface UseLocalAnalyzerResult {
  snap: AnalyzerSnap
  /** metric key → last ≤60 samples (1/s) for the sparkline canvases */
  history: ReadonlyMap<string, number[]>
  resetClip: () => void
}

/** 2 h × 8.64 MB/min × 1.5 — same sizing as the pre-flight disk check. */
const DISK_NEED_GB = Math.round(((2 * 60 * 8.64 * 1.5) / 1024) * 10) / 10

const HISTORY_MAX = 60

const EMPTY_SNAP: AnalyzerSnap = {
  rttMs: null,
  lossUpPct: null,
  lossDownPct: null,
  jitterMs: null,
  sendKbps: null,
  availOutKbps: null,
  availTrend: null,
  linkType: null,
  vidRecvKbps: null,
  vidFramesDropped: null,
  vidFreezeCount: null,
  sampleRate: null,
  workletDropped: null,
  peakDbfs: -100,
  clipCount: 0,
  recState: 'idle',
  recBytes: 0,
  recStalled: false,
  commitMs: null,
  diskFreeGB: null,
  diskNeedGB: DISK_NEED_GB,
}

interface StatLike {
  type: string
  id?: string
  [k: string]: unknown
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

export function useLocalAnalyzer(opts: {
  room: Room | null
  role: RtRole
  micStream: MediaStream | null
  recState: RecorderState
  recBytes: number
  /** actor program-feed track for the video-receive row */
  programTrack: RemoteTrack | null
  getRecorderDiag?: () => RecorderDiag | null
}): UseLocalAnalyzerResult {
  const { room, role, micStream, recState, recBytes, programTrack, getRecorderDiag } = opts

  const [snap, setSnap] = useState<AnalyzerSnap>(EMPTY_SNAP)
  const historyRef = useRef(new Map<string, number[]>())

  // mutable mirrors so the 1 s interval closure always sees fresh values
  const liveRef = useRef({ recState, recBytes, programTrack, micStream })
  liveRef.current = { recState, recBytes, programTrack, micStream }

  // ---- input peak + clip latch (AnalyserNode, ~10 Hz) ----------------------
  const peakRef = useRef(-100)
  const clipRef = useRef(0)
  useEffect(() => {
    if (!micStream || micStream.getAudioTracks().length === 0) {
      peakRef.current = -100
      return
    }
    const ctx = new AudioContext()
    const src = ctx.createMediaStreamSource(micStream)
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 2048
    src.connect(analyser)
    const buf = new Float32Array(analyser.fftSize)
    const id = setInterval(() => {
      analyser.getFloatTimeDomainData(buf)
      let peak = 0
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs(buf[i] ?? 0)
        if (v > peak) peak = v
      }
      peakRef.current = peak > 0 ? Math.max(-100, Math.round(20 * Math.log10(peak) * 10) / 10) : -100
      if (peak >= 0.999) clipRef.current += 1 // latched until resetClip
    }, 100)
    return () => {
      clearInterval(id)
      src.disconnect()
      void ctx.close().catch(() => undefined)
    }
  }, [micStream])

  // ---- 1 s sampler ---------------------------------------------------------
  useEffect(() => {
    let disposed = false
    const prev = {
      bytesSent: 0,
      packetsSent: 0,
      remoteLost: 0,
      inLost: 0,
      inRecv: 0,
      vidBytes: 0,
      tMs: 0,
      availHist: [] as number[],
      lastRecBytes: 0,
      lastRecGrowthAt: Date.now(),
      diskFreeGB: null as number | null,
      sampleRate: null as number | null,
    }
    let tickNo = 0

    // sample-rate probe: default-constructed context reflects the device rate
    try {
      const probe = new AudioContext()
      prev.sampleRate = probe.sampleRate
      void probe.close().catch(() => undefined)
    } catch {
      prev.sampleRate = null
    }

    const pollDisk = async () => {
      try {
        const est = await navigator.storage.estimate()
        const free = ((est.quota ?? 0) - (est.usage ?? 0)) / 1_073_741_824
        prev.diskFreeGB = Math.round(free * 10) / 10
      } catch {
        prev.diskFreeGB = null
      }
    }
    void pollDisk()

    const tick = async () => {
      tickNo += 1
      if (tickNo % 30 === 0) void pollDisk()

      const next: AnalyzerSnap = { ...EMPTY_SNAP }
      next.sampleRate = prev.sampleRate
      next.peakDbfs = peakRef.current
      next.clipCount = clipRef.current
      next.recState = liveRef.current.recState
      next.recBytes = liveRef.current.recBytes
      next.diskFreeGB = prev.diskFreeGB
      const diag = getRecorderDiag?.() ?? null
      next.workletDropped = diag ? diag.droppedFrames : role === 'actor' ? 0 : null
      next.commitMs = diag?.lastCommitMs ?? null

      // write-stall detector (row 13): REC + >3 s without byte growth → red
      if (next.recState === 'recording') {
        if (next.recBytes > prev.lastRecBytes) {
          prev.lastRecBytes = next.recBytes
          prev.lastRecGrowthAt = Date.now()
        }
        next.recStalled = Date.now() - prev.lastRecGrowthAt > 3000
      } else {
        prev.lastRecBytes = next.recBytes
        prev.lastRecGrowthAt = Date.now()
      }

      const nowMs = performance.now()
      const dtSec = prev.tMs > 0 ? (nowMs - prev.tMs) / 1000 : 1
      prev.tMs = nowMs

      // ---- sender side: rows 1/2/5/6/7 ------------------------------------
      try {
        const sender = room?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
          ?.sender
        if (sender) {
          const report = (await sender.getStats()) as ReadonlyMap<string, StatLike> | null
          if (report) {
            const stats = [...report.values()]
            const pair =
              stats.find((s) => s.type === 'candidate-pair' && s['nominated'] === true && s['state'] === 'succeeded') ??
              stats.find((s) => s.type === 'candidate-pair' && s['state'] === 'succeeded')
            if (pair) {
              const rtt = num(pair['currentRoundTripTime'])
              next.rttMs = rtt == null ? null : Math.round(rtt * 1000)
              const avail = num(pair['availableOutgoingBitrate'])
              if (avail != null) {
                next.availOutKbps = Math.round(avail / 1000)
                prev.availHist.push(next.availOutKbps)
                if (prev.availHist.length > 4) prev.availHist.shift()
                const h = prev.availHist
                next.availTrend =
                  h.length >= 4 && h[3]! < h[2]! && h[2]! < h[1]! && h[1]! < h[0]! ? 'down' : 'flat'
              }
              const localCand = stats.find((s) => s.id === pair['localCandidateId'])
              next.linkType = (localCand?.['candidateType'] as string | undefined) ?? null
            }
            const out = stats.find((s) => s.type === 'outbound-rtp')
            const rin = stats.find((s) => s.type === 'remote-inbound-rtp')
            if (out) {
              const bytes = num(out['bytesSent']) ?? 0
              const pkts = num(out['packetsSent']) ?? 0
              if (prev.bytesSent > 0 && bytes >= prev.bytesSent && dtSec > 0) {
                next.sendKbps = Math.round(((bytes - prev.bytesSent) * 8) / dtSec / 1000)
              }
              const lost = num(rin?.['packetsLost']) ?? prev.remoteLost
              const dPkts = pkts - prev.packetsSent
              const dLost = lost - prev.remoteLost
              if (prev.packetsSent > 0 && dPkts > 0) {
                next.lossUpPct = Math.max(0, Math.round((dLost / dPkts) * 1000) / 10)
              }
              prev.bytesSent = bytes
              prev.packetsSent = pkts
              prev.remoteLost = lost
            }
          }
        }
      } catch {
        /* mid-renegotiation — keep nulls this tick */
      }

      // ---- receiver side: rows 3/4 (first remote mic) ---------------------
      try {
        const firstRemote = room ? [...room.remoteParticipants.values()][0] : undefined
        const recvTrack = firstRemote?.getTrackPublication(Track.Source.Microphone)?.track as
          | (RemoteTrack & { receiver?: RTCRtpReceiver })
          | undefined
        const receiver = recvTrack?.receiver
        if (receiver) {
          const report = (await receiver.getStats()) as ReadonlyMap<string, StatLike> | null
          const inb = report ? [...report.values()].find((s) => s.type === 'inbound-rtp') : undefined
          if (inb) {
            const jitter = num(inb['jitter'])
            next.jitterMs = jitter == null ? null : Math.round(jitter * 1000 * 10) / 10
            const lost = num(inb['packetsLost']) ?? 0
            const recv = num(inb['packetsReceived']) ?? 0
            const dLost = lost - prev.inLost
            const dTotal = recv + lost - (prev.inRecv + prev.inLost)
            if (prev.inRecv > 0 && dTotal > 0) {
              next.lossDownPct = Math.max(0, Math.round((dLost / dTotal) * 1000) / 10)
            }
            prev.inLost = lost
            prev.inRecv = recv
          }
        }
      } catch {
        /* no remote yet */
      }

      // ---- program-video receive: row 8 (actor end) -----------------------
      try {
        const vt = liveRef.current.programTrack as
          | (RemoteTrack & { receiver?: RTCRtpReceiver })
          | null
        const receiver = vt?.receiver
        if (receiver) {
          const report = (await receiver.getStats()) as ReadonlyMap<string, StatLike> | null
          const inb = report ? [...report.values()].find((s) => s.type === 'inbound-rtp') : undefined
          if (inb) {
            const bytes = num(inb['bytesReceived']) ?? 0
            if (prev.vidBytes > 0 && bytes >= prev.vidBytes && dtSec > 0) {
              next.vidRecvKbps = Math.round(((bytes - prev.vidBytes) * 8) / dtSec / 1000)
            }
            prev.vidBytes = bytes
            next.vidFramesDropped = num(inb['framesDropped'])
            next.vidFreezeCount = num(inb['freezeCount'])
          }
        } else {
          prev.vidBytes = 0
        }
      } catch {
        /* video stats unavailable */
      }

      if (disposed) return

      // ---- history rings (sparklines) --------------------------------------
      const hist = historyRef.current
      const push = (key: string, v: number | null) => {
        const arr = hist.get(key) ?? []
        arr.push(v ?? Number.NaN)
        if (arr.length > HISTORY_MAX) arr.shift()
        hist.set(key, arr)
      }
      push('rtt', next.rttMs)
      push('lossUp', next.lossUpPct)
      push('lossDown', next.lossDownPct)
      push('jitter', next.jitterMs)
      push('sendKbps', next.sendKbps)
      push('availOut', next.availOutKbps)
      push('vidRecv', next.vidRecvKbps)
      push('peak', next.peakDbfs)
      push('recMB', next.recBytes / 1_048_576)
      push('commit', next.commitMs)
      push('diskGB', next.diskFreeGB)

      // ---- heartbeat summary → bus -----------------------------------------
      setAnalyzerSummary({
        rttMs: next.rttMs,
        lossUpPct: next.lossUpPct,
        lossDownPct: next.lossDownPct,
        jitterMs: next.jitterMs,
        bitrateKbps: next.sendKbps,
        sampleRate: next.sampleRate,
        diskFreeGB: next.diskFreeGB,
        recBytes: next.recBytes,
      })

      setSnap(next)
    }

    const id = setInterval(() => void tick(), 1000)
    void tick()
    return () => {
      disposed = true
      clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, role])

  return {
    snap,
    history: historyRef.current,
    resetClip: () => {
      clipRef.current = 0
    },
  }
}

export { DISK_NEED_GB }
